import { DEFAULT_MEDIA_VIDEO_ASPECT_RATIO } from "pages/media/constants";

export const BORDER_COLOR = "#1894FE";

export const HEADER_HEIGHT = 64;
export const MENU_TABS_HEADER_HEIGHT = 46;

// Top gap kept above the editable template when it scrolls into view, now that
// the floating settings toolbar no longer occupies that space.
export const SCENE_SCROLL_MARGIN_TOP = 24;
export const PANE_RESIZE_HANDLE_HEIGHT = 10;
export const AUDIO_VIDEO_CONTROLS_HEIGHT = 40;
export const CHAPTERS_PREVIEW_CONTAINER_PADDING = 5;

// Scene canvas zoom, expressed as a percentage. The factor stored in state is
// `percent / 100`. Zoom is purely visual: it scales the rendered preview only,
// never the relative item sizes saved in the template.
export const ZOOM_MIN_PERCENT = 50;
export const ZOOM_MAX_PERCENT = 200;
export const ZOOM_STEP_PERCENT = 25;

export const DEFAULT_WIDTH = 320;
export const DEFAULT_HEIGHT = Math.round(
  DEFAULT_WIDTH / DEFAULT_MEDIA_VIDEO_ASPECT_RATIO
); // exact 9 : 16 ratio

export const DEFAULT_BOARD_PREVIEW_WIDTH = 200;
export const DEFAULT_BOARD_PREVIEW_HEIGHT = Math.round(
  DEFAULT_BOARD_PREVIEW_WIDTH / DEFAULT_MEDIA_VIDEO_ASPECT_RATIO
);

export const DEFAULT_SMALL_PREVIEW_WIDTH = 150;

export const DEFAULT_SIZE_WITH_NULL_HEIGHT = {
  width: 0,
  height: null,
};

export const DEFAULT_POSITION = {
  x: 0,
  y: 0,
};
export const DEFAULT_PRESENTER_RELATIVE_HEIGHT = 0.4;
const DEFAULT_PRESENTER_RELATIVE_WIDTH = Math.round(
  (DEFAULT_PRESENTER_RELATIVE_HEIGHT * DEFAULT_HEIGHT) / DEFAULT_WIDTH
);
export const DEFAULT_PRESENTER_X_RELATIVE_POSITION =
  0.5 - DEFAULT_PRESENTER_RELATIVE_WIDTH / 2;
export const DEFAULT_TITLE_RELATIVE_FONT_SIZE = 23;
export const DEFAULT_SUBTITLE_RELATIVE_FONT_SIZE = 10;
export const DEFAULT_PARAGRAPH_RELATIVE_FONT_SIZE = 8;
export const DEFAULT_TITLE_RELATIVE_WIDTH = 0.83;
const DEFAULT_TITLE_RELATIVE_HEIGHT = 1;
export const DEFAULT_TITLE_LINE_HEIGHT = 0.02;
export const DEFAULT_SUBTITLES_LINE_HEIGHT = 0.05;
export const DEFAULT_SUBTITLES_RELATIVE_WIDTH = 0.9;
export const DEFAULT_SUBTITLES_RELATIVE_HEIGHT = 0.35;
const DEFAULT_SUBTITLES_X_RELATIVE_POSITION =
  0.5 - DEFAULT_SUBTITLES_RELATIVE_WIDTH / 2;
export const DEFAULT_SUBTITLES_Y_RELATIVE_POSITION = 0.2;
const DEFAULT_TITLE_X_RELATIVE_POSITION =
  0.5 - DEFAULT_TITLE_RELATIVE_WIDTH / 2;
export const DEFAULT_TITLE_Y_RELATIVE_POSITION = 0.3;
export const DEFAULT_HEADER_HEIGHT = 0.1;
export const DEFAULT_LOGO_RELATIVE_HEIGHT = 0.09;

export const DEFAULT_SUBTITLES_POSITION = {
  x: DEFAULT_SUBTITLES_X_RELATIVE_POSITION,
  y: DEFAULT_SUBTITLES_Y_RELATIVE_POSITION,
};
export const DEFAULT_SUBTITLES_SIZE = {
  width: DEFAULT_SUBTITLES_RELATIVE_WIDTH,
  height: DEFAULT_SUBTITLES_RELATIVE_HEIGHT,
};

export const DEFAULT_PRESENTER_SIZE = {
  width: DEFAULT_PRESENTER_RELATIVE_WIDTH,
  height: DEFAULT_PRESENTER_RELATIVE_HEIGHT,
};
export const DEFAULT_PRESENTER_POSITION = {
  x: DEFAULT_PRESENTER_X_RELATIVE_POSITION,
  y: 1 - DEFAULT_PRESENTER_RELATIVE_HEIGHT, //sticky to the bottom,
};

export const DEFAULT_TEXT_POSITION = {
  x: DEFAULT_SUBTITLES_X_RELATIVE_POSITION,
  y: DEFAULT_SUBTITLES_Y_RELATIVE_POSITION,
};
export const DEFAULT_TEXT_SIZE = {
  width: DEFAULT_SUBTITLES_RELATIVE_WIDTH,
  height: 0.15,
};

export const DEFAULT_TITLE_POSITION = {
  x: DEFAULT_TITLE_X_RELATIVE_POSITION,
  y: DEFAULT_TITLE_Y_RELATIVE_POSITION,
};
export const DEFAULT_TITLE_SIZE = {
  width: DEFAULT_TITLE_RELATIVE_WIDTH,
  height: DEFAULT_TITLE_RELATIVE_HEIGHT,
};

export const DEFAULT_LOGO_POSITION = DEFAULT_POSITION;
export const DEFAULT_LOGO_SIZE = {
  width: 0,
  height: DEFAULT_LOGO_RELATIVE_HEIGHT,
};

export const DEFAULT_HEADER_POSITION = DEFAULT_POSITION;
export const DEFAULT_HEADER_SIZE = { width: 1, height: DEFAULT_HEADER_HEIGHT };

export const DEFAULT_IMAGE_RELATIVE_HEIGHT = 0.3;
export const DEFAULT_VIDEO_RELATIVE_HEIGHT = 0.5;

export const CACHE_TIME_ASSETS = 5 * 60 * 1000; // 5 minutes

export const DIMENSION_PRECISION = 3;
export const DEFAULT_RELATIVE_PADDING = 0.5;
export const CUSTOM_TEXT_RELATIVE_PADDING = 0.5;

export const VIDEO_TAB_BREAKPOINTS = { 350: 2 };
export const DEFAULT_TITLE_CONTENT = "{chapter.topic}"; //needs to be hardcoded like this

export const NULL_COORDINATES: [number, number] = [0, 0];
export const NULL_COLOR = "#00000000";
export const DEFAULT_COLOR = "#ffffff";
export const DEFAULT_TEXT_COLOR = "#000000";
export const DEFAULT_STROKE_COLOR = "#000000";

export const DEFAULT_PRESENTER_LAYER_INDEX = 4;
export const DEFAULT_TITLE_LAYER_INDEX = 1;
export const DEFAULT_LOGO_LAYER_INDEX = 2;
export const DEFAULT_HEADER_LAYER_INDEX = 1;
export const DEFAULT_SUBTITLES_LAYER_INDEX = 3;

export const DEFAULT_MUSIC_VOLUME = 0.05;
export const DEFAULT_SHAPE_SIZE = 0.3;

export const DEFAULT_LINE_THICKNESS = 0.005;

export const STORYBOARD_THUMBNAILS_WIDTH = 208;
export const STORYBOARD_THUMBNAILS_PADDING = 16;
export const LAYOUT_PREVIEW_OUTLINE_WIDTH = 3;

export const STORYBOARD_MENU_TAB_HEIGHT = 56;
export const STORYBOARD_MENU_ICON_SIZE = { width: 20, height: 20 };

// Bumped after removing the settings toolbar so the editable template grows to
// fill (most of) the freed scene height, leaving only a small padding.
export const SCENE_RELATIVE_HEIGHT_MOBILE_TABLET = 75;
export const SCENE_RELATIVE_WIDTH_DESKTOP = 44;

// Where a snapped scene page lands — just below the floating sticky toolbar.
export const SNAP_PADDING_TOP = 24;

// Vertical space the sticky toolbar reserves at the top of the scroll viewport,
// subtracted when fitting a scene to the panel.
export const SCENES_TOP_RESERVE = 104;

// Combined inline padding of a scenes page (`padding-inline: 24px 40px`).
export const SCENES_HORIZONTAL_PADDING = 64;

// Spread width (px) of the selected template's outline (`box-shadow: 0 0 0 Npx`).
// The template wrapper reserves this much margin/padding so the outline isn't
// clipped by the surrounding `overflow: hidden`.
export const TEMPLATE_OUTLINE_WIDTH = 3;

//Color Placeholders
export const PRIMARY_COLOR_PLACEHOLDER = "{brand_kit.palette.primary_color}";
export const SECONDARY_COLOR_PLACEHOLDER =
  "{brand_kit.palette.secondary_color}";
export const TITLE_COLOR_PLACEHOLDER = "{brand_kit.palette.title_color}";
export const SUBTITLE_COLOR_PLACEHOLDER = "{brand_kit.palette.subtitle_color}";
//Font Placeholders
export const TEXT_FONT_PLACEHOLDER = "{brand_kit.text_font.asset_id}";
export const TITLE_FONT_PLACEHOLDER = "{brand_kit.title_font.asset_id}";
export const SUBTITLES_FONT_PLACEHOLDER = "{brand_kit.subtitles_font.asset_id}";
//Image Placeholder
export const LOGO_PICTURE_PLACEHOLDER = "{brand_kit.logo_picture.asset_id}";
//Video Placeholders
export const INTRODUCTION_VIDEO_PLACEHOLDER =
  "{brand_kit.introduction_video.asset_id}";
export const CLOSING_VIDEO_PLACEHOLDER = "{brand_kit.closing_video.asset_id}";

export const INTRODUCTION_DURATION_PLACEHOLDER =
  "{brand_kit.introduction_video.duration}";
export const CLOSING_DURATION_PLACEHOLDER =
  "{brand_kit.closing_video.duration}";

// Media generation settings flags that toggle the brand kit intro/closing.
export const ENABLE_INTRO_KEY = "enable_intro";
export const ENABLE_CLOSING_KEY = "enable_closing";

export const TEXT_PREVIEW_SELECTOR = "[data-testid='text-font-preview'] > div";

export const VIDEO_FPS = 25;
export const MAX_INTRODUCTION_DURATION_IN_FRAMES = 15 * VIDEO_FPS;
export const MAX_CLOSING_DURATION_IN_FRAMES = 60 * VIDEO_FPS;
export const MAX_TRANSITION_DURATION_IN_FRAMES = 15 * VIDEO_FPS;
export const DEFAULT_TRANSITION_DURATION_IN_FRAMES = 75;

export const DEFAULT_ANIMATION_DURATION_SECONDS = 0.4;
export const DEFAULT_ANIMATION_DURATION_FRAMES = 10;

export const DEFAULT_TABLE_NAME = "bullet_points_list";

export const EMO_COLORS = [
  { id: "adoringly", color: "#FF85A2" },
  { id: "boldly", color: "#FF6B35" },
  { id: "cheerfully", color: "#FFD166" },
  { id: "compassionately", color: "#F4A0C2" },
  { id: "delighted", color: "#FFB347" },
  { id: "eagerly", color: "#FF9F1C" },
  { id: "elated", color: "#FFCA3A" },
  { id: "enthusiastically", color: "#FF8C42" },
  { id: "excitedly", color: "#FF6F61" },
  { id: "gratefully", color: "#A8D8B9" },
  { id: "happily", color: "#FFE066" },
  { id: "hopefully", color: "#87CEEB" },
  { id: "joyfully", color: "#FFD700" },
  { id: "lovingly", color: "#FF69B4" },
  { id: "peacefully", color: "#B5EAD7" },
  { id: "playfully", color: "#FF77A9" },
  { id: "proudly", color: "#E8871E" },
  { id: "serenely", color: "#C3E8DB" },
  { id: "triumphantly", color: "#F0A500" },
  { id: "warmly", color: "#FFAB76" },
  { id: "aggressively", color: "#D32F2F" },
  { id: "angrily", color: "#B71C1C" },
  { id: "bitterly", color: "#8B4513" },
  { id: "defensively", color: "#A0522D" },
  { id: "defiantly", color: "#C62828" },
  { id: "desperately", color: "#AD1457" },
  { id: "enviously", color: "#558B2F" },
  { id: "fearfully", color: "#7B1FA2" },
  { id: "frustrated", color: "#E65100" },
  { id: "furiously", color: "#C70039" },
  { id: "grumbling", color: "#795548" },
  { id: "icy", color: "#4FC3F7" },
  { id: "impatiently", color: "#E64A19" },
  { id: "irritably", color: "#D84315" },
  { id: "melancholically", color: "#5C6BC0" },
  { id: "panicking", color: "#FF1744" },
  { id: "reluctantly", color: "#9E9E9E" },
  { id: "resentfully", color: "#6D4C41" },
  { id: "sadly", color: "#5B7DB1" },
  { id: "scornfully", color: "#880E4F" },
  { id: "shamefully", color: "#8D6E63" },
  { id: "sorrowful", color: "#4A6FA5" },
  { id: "suspiciously", color: "#6A5ACD" },
  { id: "wickedly", color: "#4A0E4E" },
  { id: "amazed", color: "#00BCD4" },
  { id: "anxiously", color: "#CE93D8" },
  { id: "calmly", color: "#81C784" },
  { id: "cautiously", color: "#ADB5BD" },
  { id: "cheeky", color: "#FF80AB" },
  { id: "confidently", color: "#1E88E5" },
  { id: "conversational tone", color: "#78909C" },
  { id: "curiously", color: "#26C6DA" },
  { id: "deadpan", color: "#90A4AE" },
  { id: "dismissively", color: "#BDBDBD" },
  { id: "flatly", color: "#B0BEC5" },
  { id: "indecisive", color: "#BCAAA4" },
  { id: "innocently", color: "#E1BEE7" },
  { id: "mischievously", color: "#AB47BC" },
  { id: "mockingly", color: "#EC407A" },
  { id: "nervously", color: "#CE93D8" },
  { id: "passionately", color: "#E91E63" },
  { id: "questioning", color: "#29B6F6" },
  { id: "quizzically", color: "#4DD0E1" },
  { id: "reassuringly", color: "#66BB6A" },
  { id: "regretfully", color: "#7986CB" },
  { id: "sarcastically", color: "#EF5350" },
  { id: "solemnly", color: "#546E7A" },
  { id: "submissively", color: "#B0BEC5" },
  { id: "tired", color: "#9E9D9D" },
  { id: "resigned tone", color: "#8D8D8D" },
  { id: "shouts", color: "#DD2C00" },
  { id: "stammers", color: "#FFAB91" },
  { id: "whispers", color: "#CFD8DC" },
];

export const DEFAULT_SPEECH_MAX_WORDS = 15;
